/**
 * ★★ Indicadores (01/11/2026) — O QUE A TELA MONTA, num módulo puro.
 *
 * ⚠ Até aqui esta composição morava dentro de `IndicadoresPage.jsx`, e o teste de igualdade
 * teria de copiá-la. ★ Aqui ela é a MESMA que a tela usa: o teste roda este código, não uma
 * cópia dele. Nenhuma conta nova: tudo sai de `painelPorDia`/`painelDoDia`/`montarGrade`.
 *
 * ★★ AS TRÊS LEITURAS DA TELA, e por que cada uma tem o tamanho que tem:
 *   1. o RESUMO DE HOJE lê só o dia (≈ 570 documentos em Campinas, contra 21,5 mil do mês);
 *   2. a SEMANA lê as interações da semana e os agendamentos do(s) mês(es) dela;
 *   3. o MÊS lê o que sempre leu.
 *
 * ⚠⚠ Por que a semana lê os agendamentos do MÊS, e não só os da semana: o comparecimento usa
 * o `compareceu` MAIS ANTIGO do lead entre os agendamentos lidos (data na reserva e crédito).
 * Com só os da semana, um lead com dois `compareceu` no mês poderia mudar de dia ou de dono, e
 * a semana discordaria do mês. ★ São ≈ 474 documentos — as interações são o grosso.
 */

import { diaDaData, paraData } from '@/produtividade/dia';
import {
  domingoDaSemana, montarGrade, painelPorDia, registrosEmDomingo, registrosNosDias, semanasDoMes,
} from '@/produtividade/grade';
import { faixaDoMesNaClinica } from '@/dashboard/periodo';

/** Os meses ('AAAA-MM') que uma lista de dias toca, na ordem. */
export const mesesDosDias = (dias = []) => [...new Set((dias ?? []).map((d) => d.slice(0, 7)))];

/**
 * ★★ 02/11/2026 — QUEM CONTA NO AVISO "N registros caíram em domingo".
 *
 * ⚠ A SDR via o número da EQUIPE inteira no aviso, embaixo de uma grade que só mostra a linha
 * dela: o mesmo vazamento de comparativo que a A2 fechou no resumo. ★ Agora o gestor
 * (`verTodas`) continua contando a equipe toda — o alerta dele é de cobertura —, e quem não vê
 * tudo conta só os próprios registros (`equipeVisivel`). O padrão é o seguro: `false`.
 */
const quemContaNoDomingo = ({ equipe, equipeVisivel, verTodas }) => (verTodas ? equipe : equipeVisivel);

/** A grade do MÊS — exatamente a composição que a tela fazia até 31/10. */
export function gradeDoMes({
  mes, interacoes = [], leads = [], agendamentos = [], equipe = [], equipeVisivel = [],
  telefonesEquipe = [], metas = {}, hoje = null, config, agora = new Date(),
  feriados = [], verTodas = false,
} = {}) {
  const porDia = painelPorDia({
    dias: semanasDoMes(mes).flat(), interacoes, leads, agendamentos,
    equipe: equipeVisivel, telefonesEquipe, agora,
  });
  return montarGrade({
    mes, porDia, equipe: equipeVisivel, metas, hoje, config, feriados,
    // ★ Para o gestor, `equipe` inteira: o alerta é de cobertura — quantos registros DE GENTE
    // caíram fora da grade. ★★ Para a SDR, só os dela (`quemContaNoDomingo`, acima).
    registrosForaDaGrade: registrosEmDomingo(mes, interacoes, quemContaNoDomingo({ equipe, equipeVisivel, verTodas })),
  });
}

/**
 * ★★ A grade da SEMANA (`semanaDe`: segunda a sábado; o domingo é da semana que acabou).
 *
 * ★ Cada dia é montado com os agendamentos do MÊS DELE — os mesmos que a grade do mês usaria —,
 * então a célula de cada dia sai igual à do mês (teste de igualdade). A semana que atravessa
 * dois meses monta cada pedaço com o seu mês.
 *
 * @param agendamentosPorMes `{ 'AAAA-MM': [...] }` — o que `buscarAgendamentosDoMes` devolve
 * @param metasPorMes        `{ 'AAAA-MM': metas por pessoa }`
 */
export function gradeDaSemana({
  semana, interacoes = [], leads = [], agendamentosPorMes = {}, equipe = [], equipeVisivel = [],
  telefonesEquipe = [], metasPorMes = {}, hoje = null, config, agora = new Date(),
  feriados = [], verTodas = false,
} = {}) {
  const porDia = new Map();
  for (const mes of mesesDosDias(semana)) {
    const doMes = painelPorDia({
      dias: semana.filter((d) => d.startsWith(mes)), interacoes, leads,
      agendamentos: agendamentosPorMes[mes] ?? [], equipe: equipeVisivel, telefonesEquipe, agora,
    });
    for (const [d, painel] of doMes) porDia.set(d, painel);
  }
  return montarGrade({
    semana, porDia, equipe: equipeVisivel, metasPorMes, hoje, config, feriados,
    registrosForaDaGrade: registrosNosDias(
      [domingoDaSemana(semana)], interacoes, quemContaNoDomingo({ equipe, equipeVisivel, verTodas }),
    ),
  });
}

/**
 * ⚠ `painelDoDia` devolve TODA pessoa com função de venda, inclusive zerada — de propósito,
 * porque na GRADE a linha ausente esconderia justamente quem precisa vê-la.
 * ★ No resumo é o contrário: um botão "copiar" que produz "Ligações: 0 em 0 leads" é ruído
 * colado no grupo. Então o resumo só aparece para quem teve movimento hoje.
 */
export const temMovimento = (l) => Object.entries(l).some(
  ([k, v]) => typeof v === 'number' && v > 0 && k !== 'valor',
) || Number(l.valor) > 0;

/**
 * ★★ Os leads que PODEM ter comparecimento hoje — e por isso precisam dos agendamentos do mês:
 *   1. o carimbo (`compareceuEm`) é de HOJE: a data é hoje, e o CRÉDITO vem do `compareceu`
 *      mais antigo do lead, que pode ser de outro dia do mês;
 *   2. SEM carimbo e com um `compareceu` entre os agendamentos do dia: a data é o `compareceu`
 *      mais antigo do mês, que pode não ser o de hoje.
 * ★ Carimbo de OUTRO dia não entra: a data é o carimbo, nos dois caminhos, e não é hoje. Lead que
 * não está na memória também não: `comparecimentosNoPeriodo` só percorre os leads da memória.
 */
export function leadsComComparecimentoPossivel({ dia, leads = [], agendamentosDoDia = [] } = {}) {
  const ids = new Set();
  const semCarimbo = new Set();
  for (const l of leads ?? []) {
    if (!l?.id) continue;
    const carimbo = paraData(l.compareceuEm);
    if (!carimbo) semCarimbo.add(l.id);
    else if (diaDaData(carimbo) === dia) ids.add(l.id);
  }
  for (const a of agendamentosDoDia ?? []) {
    if (a?.status === 'compareceu' && semCarimbo.has(a.leadId)) ids.add(a.leadId);
  }
  return [...ids];
}

/**
 * ★★ Os agendamentos do resumo: os do dia + os desses leads que a consulta do MÊS traria
 * (`criadoEm` ou `dataHora` no mês). ★ É o que mantém o crédito do comparecimento igual ao da
 * grade sem ler o mês inteiro.
 */
export function agendamentosDoResumo({ dia, agendamentosDoDia = [], agendamentosDosLeads = [] } = {}) {
  const { de, ate } = faixaDoMesNaClinica(dia.slice(0, 7));
  const noMes = (v) => {
    const d = paraData(v);
    return Boolean(d) && d >= de && d <= ate;
  };
  const porId = new Map((agendamentosDoDia ?? []).map((a) => [a.id, a]));
  for (const a of agendamentosDosLeads ?? []) {
    if (!porId.has(a.id) && (noMes(a.criadoEm) || noMes(a.dataHora))) porId.set(a.id, a);
  }
  return [...porId.values()];
}

/**
 * ★ As linhas do resumo de HOJE, a partir só dos dados do dia.
 *
 * ★★ A2 (02/11/2026) — SÓ AS LINHAS DA EQUIPE VISÍVEL, salvo `verTodas` (o gestor).
 * ⚠ `painelDoDia` cria linha para quem recebe CRÉDITO, mesmo fora da equipe que recebeu: o
 * fechamento vai para o negociador do lead e o comparecimento sem agendamento da equipe cai no
 * SDR do lead. A grade não sofre com isso (monta só as linhas da equipe visível); o resumo usava
 * `porPessoa` direto, e a SDR via e copiava a linha do negociador, com o valor fechado
 * (`docs/indicadores-testes.md` §6.1). ★ Para o gestor nada muda: `verTodas` devolve as mesmas
 * linhas de antes, inclusive a de quem já saiu da equipe.
 */
export function resumoDeHoje({
  dia, interacoes = [], agendamentos = [], leads = [], equipeVisivel = [], telefonesEquipe = [],
  agora = new Date(), verTodas = false,
} = {}) {
  const porDia = painelPorDia({
    dias: [dia], interacoes, leads, agendamentos, equipe: equipeVisivel, telefonesEquipe, agora,
  });
  const visiveis = new Set((equipeVisivel ?? []).map((u) => u.id));
  return (porDia.get(dia)?.porPessoa ?? [])
    .filter(temMovimento)
    .filter((l) => verTodas || visiveis.has(l.id));
}

